import React, { useState } from "react";
import { authService } from "../services/auth.service";

interface AuthFormProps {
  onSuccess: () => void;
}

export function AuthForm({ onSuccess }: AuthFormProps) {
  const [isLogin, setIsLogin] = useState(true);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // Validate input
      if (!username.trim() || username.trim().length < 3) {
        setError("Username must be at least 3 characters");
        setLoading(false);
        return;
      }

      if (!password || password.length < 6) {
        setError("Password must be at least 6 characters");
        setLoading(false);
        return;
      }

      if (!isLogin && password !== confirmPassword) {
        setError("Passwords do not match");
        setLoading(false);
        return;
      }

      // Authenticate
      if (isLogin) {
        await authService.login(username.trim(), password);
      } else {
        await authService.register(username.trim(), password);
      }

      // Clear password fields immediately after successful auth
      setPassword("");
      setConfirmPassword("");

      // Call success callback
      onSuccess();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Authentication failed";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h1 style={styles.title}>🃏 UNO Multiplayer</h1>
        </div>
        <p style={styles.subtitle}>
          {isLogin ? "Login to your account" : "Create a new account"}
        </p>

        {error && <div style={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit} style={styles.form}>
          <input
            type="text"
            placeholder="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            style={styles.input}
            maxLength={20}
            autoFocus
            disabled={loading}
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={styles.input}
            disabled={loading}
          />
          {!isLogin && (
            <input
              type="password"
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={styles.input}
              disabled={loading}
            />
          )}
          <button type="submit" style={styles.buttonPrimary} disabled={loading}>
            {loading ? "..." : isLogin ? "Login" : "Register"}
          </button>
        </form>

        <div style={styles.toggle}>
          <button
            type="button"
            onClick={() => {
              setIsLogin(!isLogin);
              setError("");
              setPassword("");
              setConfirmPassword("");
            }}
            style={styles.toggleButton}
            disabled={loading}
          >
            {isLogin
              ? "Don't have an account? Register"
              : "Already have an account? Login"}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "var(--bg)",
    padding: "20px",
  },
  card: {
    background: "var(--bg-card)",
    // backdropFilter removed for Casual Party (solid backgrounds, no blur)
    borderRadius: "16px",
    padding: "40px",
    boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
    maxWidth: "400px",
    width: "100%",
    border: "1px solid var(--border-subtle)",
  },
  header: {
    textAlign: "center",
    marginBottom: "20px",
  },
  title: {
    fontSize: "32px",
    fontWeight: "bold",
    margin: "0 0 10px 0",
    color: "var(--text)",
  },
  subtitle: {
    fontSize: "16px",
    color: "var(--text-muted)",
    margin: "0 0 30px 0",
    textAlign: "center",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "15px",
  },
  input: {
    padding: "12px 16px",
    fontSize: "16px",
    border: "2px solid var(--border-medium)",
    borderRadius: "8px",
    outline: "none",
    transition: "border-color 0.2s",
    background: "var(--bg-card-hover)",
    color: "var(--text)",
  },
  buttonPrimary: {
    padding: "12px 24px",
    fontSize: "16px",
    fontWeight: "bold",
    color: "white",
    background: "var(--accent)",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "transform 0.2s, box-shadow 0.2s",
    marginTop: "10px",
  },
  error: {
    padding: "12px",
    background: "rgba(231, 76, 60, 0.2)",
    color: "#ffcccc",
    borderRadius: "8px",
    marginBottom: "15px",
    fontSize: "14px",
    border: "1px solid var(--card-red)",
  },
  toggle: {
    marginTop: "20px",
    textAlign: "center",
  },
  toggleButton: {
    background: "none",
    border: "none",
    color: "#9f7aea",
    cursor: "pointer",
    fontSize: "14px",
    textDecoration: "underline",
    padding: "5px",
  },
};
